import { SnackbarEvents } from "@gtmhub/shared/components/ui/snackbar/snackbar.events";
import { IComponentOptions, IController, IPromise, IQService } from "angular";
import {
  getSinglePlugin,
  IPlugin,
  UIPluginSettings,
  IPluginSettingsUI,
  IPluginsStoreState,
  PluginsActions,
  PluginService,
  UIPluginSettingsOption,
  UIPluginOptionColor,
} from "@gtmhub/plugins";
import { INgRedux } from "@gtmhub/state-management";
import { GtmhubController } from "@gtmhub/core";
import { IIndicator, IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";

const bindStateToScope = (state: IPluginsStoreState, pluginId: string): IPluginsRedux => ({
  plugin: getSinglePlugin(state.plugins.items, pluginId),
  reduxItemsFetched: state.plugins.isFetched,
  reduxItemsError: state.plugins.error,
});

const defaultUIPluginOptionColor: UIPluginOptionColor = "#D8E1EE";
interface IPluginsRedux {
  plugin: IPlugin;
  reduxItemsFetched: boolean;
  reduxItemsError: IUIError;
}
export class PluginSettingsCtrl extends GtmhubController implements IController {
  private snackbarEvents: SnackbarEvents;
  public pluginId: string;

  public plugin: IPlugin;
  public reduxItemsFetched: boolean;
  public reduxItemsError: IUIError;

  public pluginSettings: IPluginSettingsUI;
  public indicators: {
    loadingPlugin?: IIndicator;
    loadingSettings?: IIndicator;
    savingSettings?: IIndicator;
  };

  static $inject = ["$scope", "$q", "$ngRedux", "PluginsActions", "PluginService", "UIErrorHandlingService"];
  constructor(
    private $scope: IExtendedRootScopeService,
    private $q: IQService,
    private $ngRedux: INgRedux,
    private pluginsActions: PluginsActions,
    private pluginService: PluginService,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {
    super();

    this.snackbarEvents = new SnackbarEvents($scope);
    this.indicators = {
      loadingPlugin: {
        progress: true,
      },
    };
  }

  $onInit(): void {
    this.onDestroy(this.$ngRedux.connect((state) => bindStateToScope(state as IPluginsStoreState, this.pluginId))(this));
    this.$ngRedux.dispatch(this.pluginsActions.getPlugins());

    this.$scope
      .watchUntilAndRejectOnError(
        () => this.reduxItemsFetched,
        () => this.reduxItemsError
      )
      .then(
        () => {
          delete this.indicators.loadingPlugin;
          this.getPluginSettings(this.plugin.uiPlugin.pluginSettings, this.plugin.pluginId).then((pluginSettings) => {
            this.pluginSettings = this.filterEmptyPluginSettings(this.plugin.uiPlugin.pluginSettings, pluginSettings as IPluginSettingsUI);
          });
        },
        () => {
          delete this.indicators.loadingPlugin;
          this.uiErrorHandlingService.handleModal(this.reduxItemsError);
        }
      );
  }

  pageTitle(): string {
    return this.plugin && this.plugin.title;
  }

  // Exclude empty plugin store keys in case the corresponding plugin settings type is changed via Marketplace
  // This filter is required since you can completely remove a setting from the plugin store from marketplace app,
  // but setting can still be living in the plugin model, if so, setting value should be "" initially
  // and upon changing it we should create [POST] a new setting in the plugin store
  filterEmptyPluginSettings(uiPluginSettings: UIPluginSettings[], storePluginSettings: IPluginSettingsUI): IPluginSettingsUI {
    return uiPluginSettings.reduce((newSettings, setting) => {
      if (storePluginSettings[setting.key]) {
        return {
          ...newSettings,
          [setting.key]: storePluginSettings[setting.key],
        };
      }

      return {
        ...newSettings,
        [setting.key]: { value: "", oldValue: "", updated: false },
      };
    }, {});
  }

  findSelectedColor(settings: UIPluginSettings): UIPluginOptionColor {
    return settings.pluginOptions.find((pluginOption) => pluginOption.value === this.pluginSettings[settings.key].value)?.color || defaultUIPluginOptionColor;
  }

  checkIfRequiredPluginSettingsAreFilled(): boolean {
    return this.plugin.uiPlugin.pluginSettings.some((setting) => setting.required && this.pluginSettings[setting.key] && this.pluginSettings[setting.key].value === "");
  }

  resetSettings(settings: UIPluginSettings): void {
    this.pluginSettings[settings.key].value = "";
    this.pluginSettings[settings.key].updated = true;
  }

  saveSettings(): void {
    this.indicators.savingSettings = { progress: true };
    const updatedSettings = Object.keys(this.pluginSettings).reduce((updatedSettingsMap, key) => {
      if (this.pluginSettings[key].value !== this.pluginSettings[key].oldValue) {
        updatedSettingsMap[key] = this.pluginSettings[key];
      }

      return updatedSettingsMap;
    }, {});

    this.storeSettings(updatedSettings).then(
      () => {
        delete this.indicators.savingSettings;
        this.pluginSettings = this.updateSettingsOnSave(this.pluginSettings);
        this.snackbarEvents.broadcastShowSnackbar("app_settings_updated");
      },
      (error) => {
        this.indicators.savingSettings = { error };
      }
    );
  }

  generateSecureValue(params: { value: string; updated: boolean }): string {
    if (params.updated) {
      return params.value;
    }

    return params.value ? "*************" : "";
  }

  onSettingsInputChange(settingsKey: string): void {
    this.pluginSettings[settingsKey].updated = true;
  }

  onSettingsOptionChange(settingsKey: string, option: UIPluginSettingsOption): void {
    this.pluginSettings[settingsKey].value = option.value;
    this.pluginSettings[settingsKey].updated = true;
  }

  resetIndicator = (indicator: keyof PluginSettingsCtrl["indicators"]): void => {
    delete this.indicators[indicator];
  };

  private generateDefaultSettings(pluginSettings: UIPluginSettings[]): IPluginSettingsUI {
    return pluginSettings.reduce((pluginSettingsMap, settings) => {
      pluginSettingsMap[settings.key] = {
        value: "",
        oldValue: "",
        updated: false,
      };
      return pluginSettingsMap;
    }, {});
  }

  private getPluginSettings(pluginSettings: UIPluginSettings[], pluginId: string): IPromise<IPluginSettingsUI | void> {
    this.indicators.loadingSettings = { progress: true };

    /**
     * A setting can exist in the plugin, but missing from the store, therefore the store could return 404, which in this business logic should not be error
     * handled as it’s considered as a valid case, that’s why we need to catch with angular.identity in order to resolve the promises without the ones
     * that are rejected with 404. We then use filterEmptyPluginSettings() to exclude undefined keys by replacing them with default values
     * until they are changed from UI and updated properly in plugin store.
     * */
    const settingsPromises = pluginSettings
      .filter((setting) => setting.key)
      .map((settings) => this.pluginService.getPluginStore({ pluginId, key: settings.key }).catch(angular.identity));

    return this.$q.all(settingsPromises).then(
      (settingsData) => {
        delete this.indicators.loadingSettings;
        return settingsData.reduce((settingsMap, settings) => {
          settingsMap[settings.key] = {
            value: settings.value,
            oldValue: settings.value,
            updated: false,
          };
          return settingsMap;
        }, {});
      },
      (error) => {
        if (error.status === 404 && !error.data.includes("page not found")) {
          delete this.indicators.loadingSettings;
          return this.generateDefaultSettings(pluginSettings);
        }

        this.indicators.loadingSettings = { error };
      }
    );
  }

  private storeSettings(pluginSettings: IPluginSettingsUI): IPromise<unknown> {
    const settingsPromises = Object.keys(pluginSettings).reduce((promiseColl, key) => {
      promiseColl.push(this.pluginService.setPluginStore({ pluginId: this.plugin.pluginId, key, value: pluginSettings[key].value }));
      return promiseColl;
    }, []);

    return this.$q.all(settingsPromises);
  }

  private updateSettingsOnSave(pluginSettings: IPluginSettingsUI) {
    return Object.keys(pluginSettings).reduce((updatedSettingsMap, key) => {
      updatedSettingsMap[key] = {
        value: pluginSettings[key].value,
        oldValue: pluginSettings[key].value,
        updated: false,
      };

      return updatedSettingsMap;
    }, {});
  }
}

export const PluginSettingsComponent: IComponentOptions = {
  controller: PluginSettingsCtrl,
  bindings: {
    pluginId: "@",
  },
  templateUrl: require("./plugin-settings.component.jade"),
};
